"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Smartphone } from "lucide-react";

export default function LoyaltyWalletScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
                {t("No card to carry · Tracked by phone", "Sin tarjeta · Por número de teléfono")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal mb-6">
                {t("Lives on their ", "Vive en su ")}
                <br />
                <span className="text-redy-charcoal/30">{t("phone number.", "número de teléfono.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mb-6">
                {t(
                  "Plastic punch card → fridge magnet → lost. In Redy, points live on the customer's phone number: they give it at checkout, the order earns points, and the POS shows how close they are to a reward. Customers who opted in to texts can get one with their new balance.",
                  "Tarjeta de cartón → imán de refri → perdida. En Redy, los puntos viven en el número de teléfono del cliente: lo da al pagar, la orden suma puntos y el POS muestra qué tan cerca está de un premio. Los clientes que aceptaron recibir SMS pueden recibir uno con su nuevo saldo."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-[#FAFAF8] px-4 py-3 rounded-full border border-black/5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(244,114,43,0.12)" }}>
                  <Smartphone className="w-3.5 h-3.5" style={{ color: "#F4722B" }} />
                </div>
                <div className="text-[13px] font-bold text-redy-charcoal">
                  {t("Phone number · Points at checkout · No app to download", "Número de teléfono · Puntos al pagar · Sin app que descargar")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Phone lock-screen mockup */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="flex justify-center"
            >
              <IPhoneFrame>
                <div className="h-full flex flex-col text-white relative" style={{ background: "linear-gradient(180deg, #1A1A1A 0%, #2A1A1A 100%)" }}>
                  {/* Status bar */}
                  <div className="h-7 flex items-center justify-between px-4 text-[8px] font-bold">
                    <span>7:42</span>
                    <span>5G</span>
                  </div>

                  {/* Time + date */}
                  <div className="text-center pt-8 pb-6">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-white/55">
                      Wednesday · Sep 18
                    </div>
                    <div className="text-[64px] font-black leading-none mt-1">7:42</div>
                  </div>

                  {/* Text-message notification (loyalty balance SMS, opt-in only) */}
                  <div className="px-3 mb-3">
                    <motion.div
                      initial={{ opacity: 0, y: -15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                      className="bg-white/15 backdrop-blur-md rounded-[14px] p-3 border border-white/10"
                    >
                      <div className="flex items-center gap-2 text-[9px] font-bold text-white/65 mb-1">
                        <div
                          className="w-3 h-3 rounded-sm flex items-center justify-center"
                          style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
                        >
                          <span className="text-white text-[7px] font-black">P</span>
                        </div>
                        <span>{t("MESSAGES · NOW", "MENSAJES · AHORA")}</span>
                      </div>
                      <div className="text-[12px] font-black text-white leading-tight">
                        Pepitos
                      </div>
                      <div className="text-[10px] font-medium text-white/75 mt-0.5">
                        {t("You earned 10 points — 70 of 100. 30 more for a free taco.", "Ganaste 10 puntos — 70 de 100. 30 más para un taco gratis.")}
                      </div>
                    </motion.div>
                  </div>

                  {/* Points summary pinned at bottom (Redy loyalty, tracked by phone — not a Wallet pass) */}
                  <div className="px-3 mt-auto pb-6">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.6, delay: 0.9 }}
                      className="rounded-[16px] p-3.5 text-white"
                      style={{
                        background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                        boxShadow: "0 15px 40px rgba(244,114,43,0.40)",
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-[8px] font-bold uppercase tracking-widest text-white/75">
                            PEPITOS
                          </div>
                          <div className="text-[11px] font-black">{t("Points · 70/100", "Puntos · 70/100")}</div>
                        </div>
                        <div className="text-[8px] font-bold uppercase tracking-widest text-white/75">
                          (214) ···-0142
                        </div>
                      </div>
                      <div className="flex gap-0.5">
                        {Array.from({ length: 10 }).map((_, j) => {
                          const filled = j < 7;
                          return (
                            <div
                              key={j}
                              className="flex-1 h-3 rounded-[3px]"
                              style={{
                                backgroundColor: filled
                                  ? "rgba(255,255,255,0.92)"
                                  : "rgba(255,255,255,0.18)",
                              }}
                            />
                          );
                        })}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </IPhoneFrame>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[260px] h-[520px] bg-[#0a0a0a] rounded-[40px] p-[6px] shadow-[0_40px_100px_rgba(0,0,0,0.18)] relative">
      <div className="w-full h-full rounded-[34px] overflow-hidden bg-black relative">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}
